import { useMemo, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { useRouter, usePathname } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { useBuckets } from "../hooks";
import { summariseBuckets, type ClassName, type Period } from "../utils/summarise-buckets";
import { PeriodDropdown } from "./PeriodDropdown.web";

interface ScenarioDetailPanelProps {
  siteId: SiteId;
  assetCameraKey: AssetCameraKey;
  scenarioUuid: string;
  scenarioName: string;
  cameraDisplayName: string;
}

const CLASS_LABELS: Record<ClassName, string> = {
  car: "Cars",
  human: "People",
  bike: "Bikes",
  bus: "Buses",
  truck: "Trucks",
  otherVehicle: "Other",
};

export function ScenarioDetailPanel({
  siteId,
  assetCameraKey,
  scenarioUuid,
  scenarioName,
  cameraDisplayName,
}: ScenarioDetailPanelProps) {
  const { theme } = useUnistyles();
  const router = useRouter();
  const pathname = usePathname();
  const [period, setPeriod] = useState<Period>("7d");

  const { data: buckets, isLoading, error } = useBuckets(siteId, assetCameraKey, scenarioUuid, period);
  const summary = useMemo(() => (buckets ? summariseBuckets(buckets, period) : null), [buckets, period]);

  const handleClose = () => router.replace(pathname as never);

  return (
    <View flex={1} gap="3xl">
      <View flexDirection="row" justifyContent="space-between" alignItems="flex-start" gap="lg">
        <View flex={1} gap="xs">
          <Text textStyle="lg/bold" color="text" numberOfLines={2}>
            {scenarioName}
          </Text>
          <Text textStyle="sm/regular" color="textMuted" numberOfLines={1}>
            {cameraDisplayName}
          </Text>
        </View>
        <Pressable
          onPress={handleClose}
          accessibilityRole="button"
          accessibilityLabel="Close scenario details"
          style={{ padding: theme.spacing.xs }}
        >
          <Icon name="close" size={theme.spacing["5xl"]} color={theme.colors.icon} />
        </Pressable>
      </View>

      <View>
        <PeriodDropdown value={period} onChange={setPeriod} />
      </View>

      {isLoading && (
        <View flex={1} alignItems="center" justifyContent="center">
          <ActivityIndicator />
        </View>
      )}

      {error && !buckets && (
        <View flex={1} alignItems="center" justifyContent="center" gap="lg">
          <Icon name="motion" size={theme.spacing["7xl"]} color={theme.colors.textMuted} />
          <Text textStyle="sm/regular" color="danger">
            Failed to load traffic data
          </Text>
        </View>
      )}

      {summary && summary.rows.every((r) => r.total === 0) && (
        <View
          flex={1}
          alignItems="center"
          justifyContent="center"
          style={{
            borderWidth: 1,
            borderColor: theme.colors.borderCard,
            borderRadius: theme.spacing.md,
            padding: theme.spacing["6xl"],
            gap: theme.spacing.lg,
          }}
        >
          <Icon name="motion" size={theme.spacing["7xl"]} color={theme.colors.textMuted} />
          <Text textStyle="md/bold" color="text">
            No traffic flow data available yet
          </Text>
          <Text textStyle="sm/regular" color="textMuted" numberOfLines={3} style={{ textAlign: "center" }}>
            This camera hasn&apos;t reported any traffic-flow counts for the selected period.
          </Text>
        </View>
      )}

      {summary && summary.rows.some((r) => r.total > 0) && (
        <ScrollView style={{ flex: 1 }}>
          <View
            style={{
              borderWidth: 1,
              borderColor: theme.colors.borderCard,
              borderRadius: theme.spacing.md,
              overflow: "hidden",
            }}
          >
            <View
              flexDirection="row"
              style={{
                paddingHorizontal: theme.spacing.xl,
                paddingVertical: theme.spacing.lg,
                backgroundColor: theme.colors.surfaceCard,
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.borderCard,
              }}
            >
              <Text textStyle="xs/semibold" color="textMuted" style={{ flex: 2 }}>
                Class
              </Text>
              <Text textStyle="xs/semibold" color="textMuted" style={{ flex: 1, textAlign: "right" }}>
                Total
              </Text>
              <Text textStyle="xs/semibold" color="textMuted" style={{ flex: 1, textAlign: "right" }}>
                Avg/day
              </Text>
              <Text textStyle="xs/semibold" color="textMuted" style={{ flex: 1, textAlign: "right" }}>
                Peak hour
              </Text>
            </View>
            {summary.rows.map((row) => (
              <View
                key={row.class}
                flexDirection="row"
                style={{
                  paddingHorizontal: theme.spacing.xl,
                  paddingVertical: theme.spacing.lg,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.borderCard,
                }}
              >
                <Text textStyle="sm/regular" color="text" style={{ flex: 2 }}>
                  {CLASS_LABELS[row.class]}
                </Text>
                <Text textStyle="sm/regular" color="text" style={{ flex: 1, textAlign: "right" }}>
                  {row.total.toLocaleString()}
                </Text>
                <Text textStyle="sm/regular" color="textMuted" style={{ flex: 1, textAlign: "right" }}>
                  {row.avgPerDay.toLocaleString()}
                </Text>
                <Text textStyle="sm/regular" color="textMuted" style={{ flex: 1, textAlign: "right" }}>
                  {row.peakHour}
                </Text>
              </View>
            ))}
          </View>
        </ScrollView>
      )}
    </View>
  );
}
